// Pure whole-script asset audit projection. The LLM never receives the full
// screenplay body: it gets one compact global roster with canonical names,
// aliases, descriptions, state usage counts and a few concrete references.

type Obj = Record<string, unknown>;
const isObj = (value: unknown): value is Obj => typeof value === "object" && value !== null && !Array.isArray(value);
const asArr = (value: unknown): unknown[] => Array.isArray(value) ? value : [];
const asStr = (value: unknown): string => typeof value === "string" ? value.trim() : "";

export type AuditAssetKind = "actor" | "location" | "prop";
export type AssetAuditIssueType =
  | "duplicate_actor"
  | "duplicate_location"
  | "duplicate_prop"
  | "duplicate_state"
  | "suspicious_singleton_state"
  | "unused_state"
  | "needs_human_review";
export type AssetAuditAction = "merge" | "delete" | "review";
export type AssetAuditConfidence = "high" | "medium" | "low";

export interface AssetAuditStateSnapshot {
  address: string;
  state_id: string;
  state_name: string;
  description: string;
  format_key: string;
  reference_count: number;
  episode_count: number;
  scene_count: number;
  references: string[];
  frequency_flag: "unused" | "singleton" | null;
}

export interface AssetAuditAssetSnapshot {
  kind: AuditAssetKind;
  id: string;
  name: string;
  aliases: string[];
  description: string;
  importance: string;
  format_key: string;
  alias_format_keys: string[];
  reference_count: number;
  episode_count: number;
  scene_count: number;
  references: string[];
  states: AssetAuditStateSnapshot[];
}

export interface AssetAuditSnapshot {
  title: string;
  actors: AssetAuditAssetSnapshot[];
  locations: AssetAuditAssetSnapshot[];
  props: AssetAuditAssetSnapshot[];
}

export interface AssetAuditIssue {
  type: AssetAuditIssueType;
  asset_ids: string[];
  state_ids: string[];
  names: string[];
  evidence: string;
  recommendation: string;
  suggested_survivor: string | null;
  action: AssetAuditAction;
  confidence: AssetAuditConfidence;
}

interface MutableState extends AssetAuditStateSnapshot {
  episodeIds: Set<string>;
  sceneIds: Set<string>;
}

interface MutableAsset extends AssetAuditAssetSnapshot {
  states: MutableState[];
  episodeIds: Set<string>;
  sceneIds: Set<string>;
  stateById: Map<string, MutableState>;
}

// Formatting-only key used as deterministic evidence, not as an identity
// verdict. It folds Unicode compatibility forms, case, whitespace, punctuation
// and separators (`林有粮` = `林 有粮`); semantic variants such as aliases,
// transcription mistakes and Simplified/Traditional forms remain for the model.
export function assetAuditFormatKey(value: string): string {
  return value.normalize("NFKC").toLowerCase().replace(/[\p{White_Space}\p{P}\p{S}]+/gu, "");
}

const KIND_META = {
  actor: { list: "actors", id: "actor_id", name: "actor_name" },
  location: { list: "locations", id: "location_id", name: "location_name" },
  prop: { list: "props", id: "prop_id", name: "prop_name" },
} as const;

const pushUnique = (list: string[], value: string, limit = 8): void => {
  if (!value || list.includes(value) || list.length >= limit) return;
  list.push(value);
};

export function buildAssetAuditSnapshot(doc: unknown): AssetAuditSnapshot {
  const root = isObj(doc) ? doc : {};
  const byKind: Record<AuditAssetKind, MutableAsset[]> = { actor: [], location: [], prop: [] };
  const byId = new Map<string, MutableAsset>();

  for (const kind of ["actor", "location", "prop"] as const) {
    const meta = KIND_META[kind];
    for (const raw of asArr(root[meta.list])) {
      if (!isObj(raw)) continue;
      const id = asStr(raw[meta.id]);
      const name = asStr(raw[meta.name]);
      if (!id) continue;
      const aliases = [...new Set(asArr(raw.aliases).map(asStr).filter(Boolean))];
      const stateById = new Map<string, MutableState>();
      const states: MutableState[] = [];
      for (const stateRaw of asArr(raw.states)) {
        if (!isObj(stateRaw)) continue;
        const stateId = asStr(stateRaw.state_id);
        if (!stateId) continue;
        const stateName = asStr(stateRaw.state_name);
        const state: MutableState = {
          address: `${id}/${stateId}`,
          state_id: stateId,
          state_name: stateName,
          description: asStr(stateRaw.description),
          format_key: assetAuditFormatKey(stateName),
          reference_count: 0,
          episode_count: 0,
          scene_count: 0,
          references: [],
          frequency_flag: null,
          episodeIds: new Set(),
          sceneIds: new Set(),
        };
        states.push(state);
        stateById.set(stateId, state);
      }
      const asset: MutableAsset = {
        kind,
        id,
        name,
        aliases,
        description: asStr(raw.description),
        importance: asStr(raw.importance),
        format_key: assetAuditFormatKey(name),
        alias_format_keys: aliases.map(assetAuditFormatKey),
        reference_count: 0,
        episode_count: 0,
        scene_count: 0,
        references: [],
        states,
        episodeIds: new Set(),
        sceneIds: new Set(),
        stateById,
      };
      byKind[kind].push(asset);
      byId.set(id, asset);
    }
  }

  const stateIdsOf = (ref: Obj): string[] => {
    const plural = asArr(ref.state_ids).map(asStr).filter(Boolean);
    if (plural.length > 0) return [...new Set(plural)];
    const singular = asStr(ref.state_id);
    return singular ? [singular] : [];
  };

  const addReference = (
    kind: AuditAssetKind,
    id: string,
    stateIds: string[],
    episodeId: string,
    sceneId: string,
    addr: string,
  ): void => {
    const asset = byId.get(id);
    if (!asset || asset.kind !== kind) return;
    asset.reference_count += 1;
    asset.episodeIds.add(episodeId);
    asset.sceneIds.add(`${episodeId}/${sceneId}`);
    pushUnique(asset.references, addr);
    const resolvedStateIds = stateIds.length > 0
      ? stateIds
      : asset.stateById.has("default")
        ? ["default"]
        : [];
    for (const stateId of resolvedStateIds) {
      const state = asset.stateById.get(stateId);
      if (!state) continue;
      state.reference_count += 1;
      state.episodeIds.add(episodeId);
      state.sceneIds.add(`${episodeId}/${sceneId}`);
      pushUnique(state.references, addr);
    }
  };

  for (const episodeRaw of asArr(root.episodes)) {
    if (!isObj(episodeRaw)) continue;
    const episodeId = asStr(episodeRaw.episode_id) || "<episode>";
    for (const sceneRaw of asArr(episodeRaw.scenes)) {
      if (!isObj(sceneRaw)) continue;
      const sceneId = asStr(sceneRaw.scene_id) || "<scene>";
      const sceneAddr = `${episodeId}/${sceneId}`;
      if (isObj(sceneRaw.location)) {
        addReference(
          "location",
          asStr(sceneRaw.location.location_id),
          stateIdsOf(sceneRaw.location),
          episodeId,
          sceneId,
          `${sceneAddr}.location`,
        );
      }
      for (const ref of asArr(sceneRaw.actors)) {
        if (isObj(ref)) {
          addReference(
            "actor",
            asStr(ref.actor_id),
            stateIdsOf(ref),
            episodeId,
            sceneId,
            `${sceneAddr}.actors`,
          );
        }
      }
      for (const ref of asArr(sceneRaw.props)) {
        if (isObj(ref)) {
          addReference(
            "prop",
            asStr(ref.prop_id),
            stateIdsOf(ref),
            episodeId,
            sceneId,
            `${sceneAddr}.props`,
          );
        }
      }
      asArr(sceneRaw.actions).forEach((actionRaw, actionIndex) => {
        if (!isObj(actionRaw) || !isObj(actionRaw.speaker)) return;
        const actorId = asStr(actionRaw.speaker.actor_id);
        if (actorId) {
          addReference(
            "actor",
            actorId,
            stateIdsOf(actionRaw.speaker),
            episodeId,
            sceneId,
            `${sceneAddr}#${actionIndex + 1}.speaker`,
          );
        }
      });
    }
  }

  const finish = (asset: MutableAsset): AssetAuditAssetSnapshot => {
    asset.episode_count = asset.episodeIds.size;
    asset.scene_count = asset.sceneIds.size;
    for (const state of asset.states) {
      state.episode_count = state.episodeIds.size;
      state.scene_count = state.sceneIds.size;
      state.frequency_flag = state.state_id === "default"
        ? null
        : state.scene_count === 0
          ? "unused"
          : state.scene_count === 1
            ? "singleton"
            : null;
    }
    return {
      kind: asset.kind,
      id: asset.id,
      name: asset.name,
      aliases: asset.aliases,
      description: asset.description,
      importance: asset.importance,
      format_key: asset.format_key,
      alias_format_keys: asset.alias_format_keys,
      reference_count: asset.reference_count,
      episode_count: asset.episode_count,
      scene_count: asset.scene_count,
      references: asset.references,
      states: asset.states.map((state) => ({
        address: state.address,
        state_id: state.state_id,
        state_name: state.state_name,
        description: state.description,
        format_key: state.format_key,
        reference_count: state.reference_count,
        episode_count: state.episode_count,
        scene_count: state.scene_count,
        references: state.references,
        frequency_flag: state.frequency_flag,
      })),
    };
  };

  return {
    title: asStr(root.title),
    actors: byKind.actor.map(finish),
    locations: byKind.location.map(finish),
    props: byKind.prop.map(finish),
  };
}
